import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import { 
  House, 
  Folder, 
  Buildings, 
  Users, 
  FileText, 
  SignOut,
  Lightning,
  List,
  Envelope,
  Bell,
  PenNib,
  ChartBar,
  UserGear,
  Receipt,
  Gear
} from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/217o5tly_Marema_Logo.jpg";

const NAV_ITEMS = [
  { path: '/admin', icon: House, label: 'Dashboard', exact: true },
  { path: '/admin/cases', icon: Folder, label: 'Cases' },
  { path: '/admin/companies', icon: Buildings, label: 'Companies' },
  { path: '/admin/proxies', icon: Users, label: 'Contacts' },
  { path: '/admin/contracts', icon: FileText, label: 'Contracts' },
  { path: '/admin/quotations', icon: PenNib, label: 'Quotations' },
  { path: '/admin/invoices', icon: Receipt, label: 'Invoicing' },
  { path: '/admin/reports', icon: ChartBar, label: 'Reports' },
  { path: '/admin/email-templates', icon: Envelope, label: 'Email Templates' },
  { path: '/admin/notifications', icon: Bell, label: 'Notifications' },
  { path: '/admin/invoicing-settings', icon: Gear, label: 'Invoice Settings' },
  { path: '/admin/users', icon: UserGear, label: 'Users' },
];

const AdminLayout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#1a2332] transform transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-20 border-b border-gray-700">
            <img src={LOGO_URL} alt="Marema Consulting" className="h-16 w-auto bg-white p-1 rounded" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-item flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-none transition-colors ${
                  isActive(item.path, item.exact)
                    ? 'bg-[#4CAF50]/20 text-white border-l-4 border-[#4CAF50] -ml-[1px]'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" weight={isActive(item.path, item.exact) ? 'fill' : 'regular'} />
                {item.label}
              </Link>
            ))}
          </nav>

          {/* User */}
          <div className="px-4 py-4 border-t border-gray-700">
            <div className="flex items-center gap-3 px-4 py-2">
              <div className="w-8 h-8 bg-[#4CAF50] rounded-full flex items-center justify-center text-white font-medium text-sm">
                {user?.name?.[0]?.toUpperCase() || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full mt-2 text-gray-400 hover:text-white hover:bg-white/5 justify-start gap-3 rounded-none"
              data-testid="logout-btn"
            >
              <SignOut className="w-5 h-5" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Main content */}
      <div className="flex-1 lg:ml-64">
        {/* Top bar */}
        <header className="glass-header sticky top-0 z-30">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <List className="w-5 h-5" />
              </Button>
              <h1 className="text-lg font-semibold text-[#0A192F]">{title}</h1>
            </div>
            <Link to="/" className="text-sm text-[#4B5563] hover:text-[#0A192F]">
              View Website
            </Link>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
